import React, { useState } from 'react';
import { submitContact } from '../services/ContactService';
import { message } from 'antd';
import { useData } from '../context/DataContext';
import { normalizeImageUrl } from '../utils/imageUtils';
import { getSettingAddress } from '../utils/branchUtils';


const validateEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|((([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))$/
        );
};

const validatePhone = (phone) => {
    return String(phone).match(/^(\+84|84|0)[0-9]{8,10}$/);
};

const ContactPage = () => {
    const { branches, settings } = useData();
    const [formData, setFormData] = useState({
        fullName: '',
        phoneNumber: '',
        email: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const toastConfig = { style: { marginTop: '80px' } };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.fullName || !formData.phoneNumber) {
            message.warning({ content: 'Vui lòng nhập họ tên và số điện thoại', ...toastConfig });
            return;
        }

        if (formData.email && !validateEmail(formData.email)) {
            message.error({ content: 'Email không đúng định dạng', ...toastConfig });
            return;
        }

        if (!validatePhone(formData.phoneNumber)) {
            message.error({ content: 'Số điện thoại không đúng định dạng', ...toastConfig });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                fullName: formData.fullName || null,
                phoneNumber: formData.phoneNumber || null,
                email: formData.email || null,
                message: formData.message || null,
            };
            await submitContact(payload);
            message.success({ content: 'Cảm ơn bạn! Yêu cầu của bạn đã được gửi thành công.', ...toastConfig });
            setFormData({ fullName: '', phoneNumber: '', email: '', message: '' });
        } catch (error) {
            console.error('Error submitting contact:', error);
            message.error({ content: 'Có lỗi xảy ra, vui lòng thử lại sau.', ...toastConfig });
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="contact-page">
            {/* ════ MINIMAL BREADCRUMBS ════ */}
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider">
                        <a href="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</a>
                        <span className="opacity-30">/</span>
                        <span className="text-slate-900 font-bold">Liên hệ</span>
                    </nav>
                </div>
            </div>

            {/* ════ TOP SECTION: INFO & FORM ════ */}
            <div className="container mb-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

                    {/* LEFT SIDE: CONTACT INFO */}
                    <div className="lg:col-span-5 space-y-10">
                        <div>
                            <span className="text-red-600 font-bold uppercase tracking-[0.4em] text-[10px] mb-3 block">Get in touch</span>
                            <h1 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter leading-tight">
                                Liên hệ <br /><span className="text-red-600">với chúng tôi</span>
                            </h1>
                            <div className="w-12 h-1 bg-red-600 mt-4 rounded-full" />
                            <p className="mt-6 text-slate-500 text-base leading-relaxed max-w-sm">
                                Chúng tôi luôn sẵn sàng lắng nghe và hỗ trợ bạn 24/7. Đừng ngần ngại liên hệ để được tư vấn.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            {/* Address Card */}
                            <div className="flex gap-5 p-6 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.05)] transition-all duration-500 group">
                                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-red-600 text-lg group-hover:bg-red-600 group-hover:text-white transition-all duration-500 shrink-0">
                                    <i className="fa-solid fa-location-dot" />
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Địa chỉ trụ sở</h4>
                                    <p className="text-slate-900 font-extrabold text-base leading-tight">
                                        {getSettingAddress(settings?.address)}
                                    </p>
                                </div>
                            </div>

                            {/* Hotline Card */}
                            <div className="flex gap-5 p-6 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.05)] transition-all duration-500 group">
                                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-red-600 text-lg group-hover:bg-red-600 group-hover:text-white transition-all duration-500 shrink-0">
                                    <i className="fa-solid fa-phone-volume" />
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Hotline hỗ trợ</h4>
                                    <a href={`tel:${settings?.hotline}`} className="text-slate-900 font-extrabold text-xl hover:text-red-600 transition-colors">
                                        {settings?.hotline}
                                    </a>
                                </div>
                            </div>

                            {/* Email Card */}
                            <div className="flex gap-5 p-6 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.02)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.05)] transition-all duration-500 group">
                                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-red-600 text-lg group-hover:bg-red-600 group-hover:text-white transition-all duration-500 shrink-0">
                                    <i className="fa-solid fa-envelope-open-text" />
                                </div>
                                <div>
                                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Email liên hệ</h4>
                                    <a href={`mailto:${settings?.email || 'info@otohathanh.com'}`} className="text-slate-900 font-extrabold text-base hover:text-red-600 transition-colors">
                                        {settings?.email || 'info@otohathanh.com'}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT SIDE: CONTACT FORM */}
                    <div className="lg:col-span-7">
                        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-slate-100 shadow-[0_20px_80px_rgba(0,0,0,0.03)] relative overflow-hidden">
                            {/* Decorative background shape */}
                            <div className="absolute top-0 right-0 w-48 h-48 bg-red-50 rounded-full blur-3xl opacity-50 -mr-24 -mt-24" />

                            <div className="relative z-10">
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-8">
                                    Gửi tin nhắn <span className="text-red-600">tư vấn</span>
                                </h3>

                                <form className="space-y-5" onSubmit={handleSubmit}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-1.5">
                                            <label htmlFor="fullName" className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 ml-4">Họ và tên *</label>
                                            <input
                                                type="text"
                                                id="fullName"
                                                name="fullName"
                                                className="w-full bg-slate-50 border-none rounded-xl py-4 px-6 text-slate-900 font-bold placeholder:text-slate-300 focus:ring-4 focus:ring-red-600/5 transition-all outline-none text-sm"
                                                placeholder="Nguyễn Văn A"
                                                value={formData.fullName}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor="phoneNumber" className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 ml-4">Số điện thoại *</label>
                                            <input
                                                type="tel"
                                                id="phoneNumber"
                                                name="phoneNumber"
                                                className="w-full bg-slate-50 border-none rounded-xl py-4 px-6 text-slate-900 font-bold placeholder:text-slate-300 focus:ring-4 focus:ring-red-600/5 transition-all outline-none text-sm"
                                                placeholder="09xx xxx xxx"
                                                value={formData.phoneNumber}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="email" className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 ml-4">Địa chỉ Email</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            className="w-full bg-slate-50 border-none rounded-xl py-4 px-6 text-slate-900 font-bold placeholder:text-slate-300 focus:ring-4 focus:ring-red-600/5 transition-all outline-none text-sm"
                                            placeholder="example@gmail.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label htmlFor="message" className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 ml-4">Nội dung yêu cầu</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            className="w-full bg-slate-50 border-none rounded-xl py-4 px-6 text-slate-900 font-bold placeholder:text-slate-300 focus:ring-4 focus:ring-red-600/5 transition-all outline-none min-h-[120px] resize-none text-sm"
                                            placeholder="Tôi cần tư vấn về dịch vụ bảo dưỡng..."
                                            value={formData.message}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-slate-900 hover:bg-red-600 text-white font-black text-[10px] uppercase tracking-[0.3em] py-5 rounded-2xl transition-all duration-500 shadow-xl hover:shadow-red-600/20 disabled:opacity-50 flex items-center justify-center gap-3 group"
                                        >
                                            {loading ? (
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    Gửi yêu cầu ngay
                                                    <i className="fa-solid fa-paper-plane transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hệ thống văn phòng */}
                <div className="flex flex-col items-center mb-16" data-aos="fade-up">
                    <span className="text-red-600 font-bold uppercase tracking-[0.4em] text-[10px] mb-4">Network</span>
                    <h2 className="text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tighter text-center">
                        Hệ thống <span className="text-red-600">văn phòng</span>
                    </h2>
                    <div className="w-16 h-1 bg-red-600 mt-6 rounded-full" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                    {branches && branches.length > 0 ? (
                        branches.map((b, idx) => (
                            <div className="group" key={b._id || b.id || idx} data-aos="fade-up" data-aos-delay={idx * 150}>
                                <div className="bg-white rounded-[3.5rem] overflow-hidden border border-slate-100 shadow-[0_15px_50px_rgba(0,0,0,0.02)] hover:shadow-[0_30px_80px_rgba(0,0,0,0.12)] transition-all duration-700 hover:-translate-y-4 flex flex-col h-full">
                                    <div className="relative aspect-[16/10] overflow-hidden m-4 rounded-[2.8rem]">
                                        <img src={normalizeImageUrl(b.imageUrl)} alt={b.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                        <div className="absolute top-6 right-6">
                                            <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/30">
                                                <i className="fa-solid fa-location-dot" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-10 pt-4 flex flex-col flex-grow">
                                        <h3 className="text-xl font-black text-slate-900 mb-6 group-hover:text-red-600 transition-colors line-clamp-2 uppercase tracking-tight">
                                            {b.name}
                                        </h3>
                                        <div className="space-y-4 mb-8">
                                            <div className="flex items-start gap-3">
                                                <div className="mt-1 text-red-600"><i className="fa-solid fa-map-pin" /></div>
                                                <div className="text-slate-500 text-sm leading-relaxed">
                                                    <strong className="text-slate-900 block mb-0.5">Địa chỉ:</strong>
                                                    {b.address || 'Đang cập nhật'}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-red-600"><i className="fa-solid fa-phone" /></div>
                                                <div className="text-slate-500 text-sm">
                                                    <strong className="text-slate-900">Hotline:</strong>&nbsp;
                                                    <a href={`tel:${b.phone || settings?.hotline}`} className="hover:text-red-600 transition-colors">
                                                        {b.phone || settings?.hotline}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-auto pt-8 border-t border-slate-50">
                                            <a
                                                href={b.mapUrl || `https://www.google.com/maps/place/${(b.name + ' ' + (b.address || '')).split(' ').join('+')}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center justify-center gap-3 bg-slate-900 hover:bg-red-600 text-white font-black text-[11px] uppercase tracking-[0.2em] w-full py-5 rounded-3xl transition-all duration-500 shadow-xl group-hover:shadow-red-600/30"
                                            >
                                                Xem bản đồ <i className="fa-solid fa-arrow-right-long transition-transform group-hover:translate-x-2" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-12 text-center py-20">
                            <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Đang tải chi nhánh...</p>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default ContactPage;



